import { take } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';
import { increment } from 'api/models';
import {
  Notification,
  NotificationStatus,
  NotificationGroup,
  notifications as api,
  Start,
  Filter,
} from 'api/notifications';
import {
  Group,
  Content,
  Deleted,
  Loaded,
  ActionableGroup,
  NotificationCount,
  setGroups,
  Loading,
} from './models';

const prepend = (
  group: ActionableGroup,
  notifications: Notification[],
): ActionableGroup => ({
  ...group,
  notifications: notifications.concat(group.notifications),
});

export const makeGroup =
  (
    status: NotificationStatus,
    filter: Filter,
    count: NotificationCount,
    content$: BehaviorSubject<Content>,
  ) =>
  (subscriptions: Subscription[], group: NotificationGroup): Group => {
    const action = status === NotificationStatus.New ? api.clear : api.unclear;
    const key = status === NotificationStatus.New ? 'clear' : 'unclear';
    const notifications = take(group.notifications, count[1]);
    const loadMore =
      group.notifications.length > count[1]
        ? () => {
            const { sortKey } =
              group.notifications[group.notifications.length - 1];
            const start = Start(sortKey);
            const content = content$.value;

            if (
              content[0] === 'empty' ||
              content[0] === 'fetching_first_page' ||
              content[0] === 'fetching'
            ) {
              return;
            }
            const groups = content[2];

            content$.next(
              setGroups(
                content,
                groups.map((g) =>
                  g[1].key.uri === group.key.uri ? Loading(g[1]) : g,
                ),
              ),
            );
            const makeNextGroup = makeGroup(status, filter, count, content$);

            subscriptions.push(
              api
                .get(group.key, status, filter, increment(count), start)
                .subscribe(
                  (next) => {
                    content$.next(
                      setGroups(
                        content,
                        groups.map((g) => {
                          if (g[1].key.uri !== group.key.uri) {
                            return g;
                          }
                          const nextGroup = makeNextGroup(subscriptions, {
                            ...g[1],
                            notifications: next,
                          });

                          return Loaded(
                            prepend(nextGroup[1], g[1].notifications),
                          );
                        }),
                      ),
                    );
                  },
                  () => {
                    // TODO: Notify user of failure
                    content$.next(
                      setGroups(
                        content,
                        groups.map((g) =>
                          g[1].key.uri === group.key.uri ? Loaded(g[1]) : g,
                        ),
                      ),
                    );
                  },
                ),
            );
          }
        : undefined;
    const callback = () => {
      const content = content$.value;

      if (
        content[0] === 'empty' ||
        content[0] === 'fetching_first_page' ||
        content[0] === 'fetching'
      ) {
        return;
      }
      const groups = content[2];
      const next = setGroups(
        content,
        groups.map((g) => (g[1].key.uri === group.key.uri ? Deleted(g[1]) : g)),
      );

      content$.next(next);
      subscriptions.push(
        action(group.key, filter).subscribe(
          () => {
            const deleted = setGroups(
              content,
              groups.filter((x) => x[1].key.uri !== group.key.uri),
            );

            content$.next(deleted);
          },
          () => {
            // TODO: Notify user of failure
            content$.next(
              setGroups(
                content,
                groups.map((g) =>
                  g[1].key.uri === group.key.uri ? Loaded(g[1]) : g,
                ),
              ),
            );
          },
        ),
      );
    };

    return Loaded({
      ...group,
      notifications,
      status,
      loadMore,
      [key]: callback,
    } as ActionableGroup);
  };
